'use client'

import {
  ChangeEvent,
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { cx } from 'class-variance-authority'

type SixDigitInputProps = {
  setCode: (code: string) => void
  label?: string
  className?: string
  position?: 'center' | 'start' | 'end'
}

export const SixDigitInput = ({
  label,
  setCode,
  className,
  position = 'start',
}: SixDigitInputProps) => {
  const [codeNumber, setCodeNumber] = useState<string[]>([
    '',
    '',
    '',
    '',
    '',
    '',
  ])
  const inputs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    const newCode = codeNumber.join('')
    setCode(newCode)
  }, [codeNumber, setCode])

  useEffect(() => {
    if (inputs.current[0]) {
      inputs.current[0].focus()
    }
  }, [])

  const handleChange = (element: HTMLInputElement, index: number) => {
    if (isNaN(Number(element.value))) return

    setCodeNumber((prevCode) =>
      prevCode.map((d, idx) => (idx === index ? element.value : d)),
    )

    // Focus next input
    if (element.value && index < 5 && inputs.current[index + 1]) {
      inputs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      e.preventDefault()

      if (codeNumber[index]) {
        // If current input has a value, clear it and maintain focus
        setCodeNumber((prevCode) => {
          const newCode = [...prevCode]
          newCode[index] = ''
          return newCode
        })
        inputs.current[index]?.focus() // Ensure focus stays on the current input
      } else if (index > 0) {
        // If current input is empty and it's not the first input, move focus to previous input
        inputs.current[index - 1]?.focus()
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputs.current[index - 1]?.focus()
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasteData = e.clipboardData.getData('text').slice(0, 6).split('')
    setCodeNumber((prevCode) => {
      const newCode = [...prevCode]
      pasteData.forEach((value, index) => {
        if (index < 6) newCode[index] = value
      })
      return newCode
    })
    const focusIndex = Math.min(5, pasteData.length)
    inputs.current[focusIndex]?.focus()
  }

  return (
    <div className={cx(className)}>
      <label
        htmlFor="verificationCode"
        className="mb-1 block text-sm font-medium text-gray-700"
      >
        {label ?? 'Code de vérification'}
      </label>
      <div className={`flex items-center justify-${position}`}>
        {codeNumber.map((digit, index) => (
          <input
            key={index}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange(e.target, index)
            }
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) =>
              handleKeyDown(e, index)
            }
            onPaste={handlePaste}
            ref={(ref) => {
              inputs.current[index] = ref
            }}
            className="border-1 mx-1 h-12 w-10 rounded border-gray-300 text-center text-xl focus:border-primary-500"
          />
        ))}
      </div>
    </div>
  )
}
